import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { ResourceDataReviewComponent } from '../shared/resource-data-review/resource-data-review.component';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { ErrorService } from '../shared/error.service';
import { HealthcareServicesService } from './healthcare-services.service';
import { HealthcareService } from 'fhir/r4';
import { HealthcareServiceConversionService } from './healthcare-service-conversion.service';
import { formMetaData, FormFields, CodeableConceptField, IndividualField, SingleCodeField, IndividualReferenceField, GroupField } from '../shared/dynamic-forms.interface2';
import { AuthService } from '../shared/auth/auth.service';
import { terminologyCanonicalUrlBaseToken, specialtiesToken } from '../app.config';
import { UtilityService } from '../shared/utility.service';

export type HealthcareServiceEditorDialogData = {
    resource?: HealthcareService | null;
};

@Component({
    selector: 'app-healthcare-service-editor-dialog',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatProgressBarModule,
        MatProgressSpinnerModule,
        MatDividerModule,
        DynamicFormsV2Component,
        ResourceDataReviewComponent
    ],
    templateUrl: './healthcare-service-editor-dialog.component.html',
    styleUrl: './healthcare-service-editor-dialog.component.scss'
})
export class HealthcareServiceEditorDialogComponent {
    private readonly dialogRef = inject(MatDialogRef<HealthcareServiceEditorDialogComponent>);
    private readonly data = inject(MAT_DIALOG_DATA, { optional: true }) as HealthcareServiceEditorDialogData | null;
    private readonly selectData = inject(FormFieldsSelectDataService);
    private readonly errorService = inject(ErrorService);
    private readonly services = inject(HealthcareServicesService);
    private readonly conversion = inject(HealthcareServiceConversionService);
    private readonly auth = inject(AuthService);
    private readonly terminologyBase = inject(terminologyCanonicalUrlBaseToken);
    private readonly specialties = inject(specialtiesToken);
    private readonly utility = inject(UtilityService);

    readonly isEditMode = !!this.data?.resource;

    formFields: FormFields[] = [];
    formMeta: formMetaData = {
        formName: this.isEditMode ? 'Edit Healthcare Service' : 'New Healthcare Service',
        formDescription: 'Capture the base details for this healthcare service. Continue to review before saving.',
        submitText: 'Review Service',
        closeDialogOnSubmit: false
    };
    dynamicFormPreset: any;
    liveFormValue: any;
    reviewFormData: any;
    reviewResource?: HealthcareService;
    isSaving = false;
    optionsLoaded = false;
    private readonly defaultCategoryOptions: string[] = [
        'gov$#$Government$#$http://terminology.hl7.org/CodeSystem/service-category',
        'dept$#$Department$#$http://terminology.hl7.org/CodeSystem/service-category',
        'edu$#$Education$#$http://terminology.hl7.org/CodeSystem/service-category',
        'med$#$Medical$#$http://terminology.hl7.org/CodeSystem/service-category',
        'surg$#$Surgical$#$http://terminology.hl7.org/CodeSystem/service-category'
    ];
    private readonly defaultTypeOptions: string[] = [
        'gp$#$General practice$#$http://terminology.hl7.org/CodeSystem/service-type',
        'card$#$Cardiology$#$http://terminology.hl7.org/CodeSystem/service-type',
        'obs$#$Obstetrics$#$http://terminology.hl7.org/CodeSystem/service-type',
        'surge$#$Surgery$#$http://terminology.hl7.org/CodeSystem/service-type',
        'imaging$#$Imaging$#$http://terminology.hl7.org/CodeSystem/service-type'
    ];
    private readonly specialtyOptions = this.buildSpecialtyOptions();

    readonly mapResourceToSubmittedData = (resource: HealthcareService) =>
        this.conversion.convertFhirResourceToDynamicFormFormat(resource);

    readonly applySubmittedDataToResource = (submitted: any, resource: HealthcareService) =>
        this.conversion.convertDynamicFormFormatToFhirResource(submitted, resource);

    constructor() {
        this.dynamicFormPreset = this.isEditMode
            ? this.conversion.convertFhirResourceToDynamicFormFormat(this.data?.resource)
            : { active: 'active' };

        const telecomUse$ = this.selectData.getFormFieldSelectData('patient', 'contactPointUse')
            .pipe(catchError(() => of(['work', 'mobile', 'home'])));
        const telecomSystem$ = this.selectData.getFormFieldSelectData('patient', 'contactPointSystem')
            .pipe(catchError(() => of(['phone', 'email'])));
        const practitionerOptions$ = this.selectData.getFormFieldSelectData('healthcareService', 'practitioners')
            .pipe(catchError(() => of([])));
        const assignedPractitioners$ = this.isEditMode && this.data?.resource?.id
            ? this.services.getPractitionerAssignments(this.data.resource.id).pipe(
                map(refs => refs
                    .map(ref => this.utility.delimitedFromReference(ref) ?? ref.reference ?? '')
                    .filter((entry): entry is string => !!entry)
                ),
                catchError(() => of<string[]>([]))
            )
            : of<string[]>([]);

        forkJoin({
            telecomUse: telecomUse$,
            telecomSystem: telecomSystem$,
            practitionerOptions: practitionerOptions$,
            assignedPractitioners: assignedPractitioners$
        }).subscribe({
            next: ({ telecomUse, telecomSystem, practitionerOptions, assignedPractitioners }) => {
                this.formFields = this.buildFormFields({
                    telecomUse: telecomUse ?? [],
                    telecomSystem: telecomSystem ?? [],
                    practitionerOptions: practitionerOptions ?? []
                });
                if (assignedPractitioners?.length) {
                    this.dynamicFormPreset = {
                        ...this.dynamicFormPreset,
                        assignedPractitioners
                    };
                }
                this.optionsLoaded = true;
            },
            error: () => this.errorService.openandCloseError('Unable to load reference data for the healthcare service form.')
        });
    }

    handleFormChange(value: any): void {
        this.liveFormValue = value;
    }

    handleFormSubmit(payload: any): void {
        const values = payload?.values ?? payload ?? {};
        this.reviewFormData = this.deepClone(values);
        this.reviewResource = this.prepareResourceForReview(values);
    }

    handleFieldEdited(evt: { fieldApiName: string; newValue: any }): void {
        if (!this.reviewFormData) {
            return;
        }
        const next = this.deepClone(this.reviewFormData);
        next[evt.fieldApiName] = evt.newValue;
        this.reviewFormData = next;
        this.dynamicFormPreset = this.deepClone(next);
    }

    handleResourceUpdated(updated: HealthcareService): void {
        this.reviewResource = this.conversion.eliminateEmptyFieldsInShallowAndDeepPaths(updated);
    }

    save(): void {
        if (!this.reviewFormData) {
            this.errorService.openandCloseError('Review the form before saving.');
            return;
        }
        this.isSaving = true;
        const save$ = this.isEditMode && this.data?.resource
            ? this.services.updateHealthcareService(this.data.resource, this.reviewFormData)
            : this.services.createHealthcareService(this.reviewFormData);
        save$.pipe(catchError(err => {
            this.errorService.openandCloseError('Unable to save healthcare service right now.');
            return of(null);
        })).subscribe(result => {
            this.isSaving = false;
            if (result) {
                this.dialogRef.close(result);
            }
        });
    }

    cancel(): void {
        this.dialogRef.close();
    }

    private prepareResourceForReview(values: any): HealthcareService {
        const practitionerId = this.auth.user.getValue()?.['fhirResourceId'] ?? null;
        const resource = this.conversion.convertDynamicFormFormatToFhirResource(values, this.data?.resource);
        const withContext = this.conversion.attachAppStateContext(resource, { practitionerId });
        return this.conversion.eliminateEmptyFieldsInShallowAndDeepPaths(withContext);
    }

    private buildSpecialtyOptions(): string[] {
        return this.specialties.map(spec => {
            return [spec.code, spec.display, spec.system].join('$#$');
        });
    }
    private buildFormFields(options: { telecomUse: string[]; telecomSystem: string[]; practitionerOptions: string[]; }): FormFields[] {
        return [
            <IndividualField>{
                generalProperties: {
                    fieldApiName: 'name',
                    fieldName: 'Service name',
                    fieldLabel: 'Service name',
                    fieldType: 'IndividualField',
                    inputType: 'text',
                    isArray: false,
                    isGroup: false,
                    validations: [{ type: 'default', name: 'required', isFunction: false }]
                }
            },
            <SingleCodeField>{
                generalProperties: {
                    fieldApiName: 'active',
                    fieldName: 'Status',
                    fieldType: 'SingleCodeField',
                    isArray: false,
                    isGroup: false,
                    value: this.dynamicFormPreset?.active ?? 'active'
                },
                data: ['active', 'inactive']
            },
            <CodeableConceptField>{
                generalProperties: {
                    fieldApiName: 'category',
                    fieldName: 'Categories',
                    fieldType: 'CodeableConceptField',
                    isArray: true,
                    isGroup: false,
                    allowedOthers: true
                },
                data: this.defaultCategoryOptions
            },
            <CodeableConceptField>{
                generalProperties: {
                    fieldApiName: 'type',
                    fieldName: 'Service types',
                    fieldType: 'CodeableConceptField',
                    isArray: true,
                    isGroup: false,
                    allowedOthers: true
                },
                data: this.defaultTypeOptions
            },
            <CodeableConceptField>{
                generalProperties: {
                    fieldApiName: 'specialty',
                    fieldName: 'Specialties',
                    fieldType: 'CodeableConceptField',
                    isArray: true,
                    isGroup: false,
                    allowedOthers: true
                },
                data: this.specialtyOptions
            },
            <GroupField>{
                generalProperties: {
                    fieldApiName: 'telecom',
                    fieldName: 'Contacts',
                    fieldType: 'IndividualField',
                    isArray: true,
                    isGroup: true
                },
                keys: ['system', 'use', 'value'],
                groupFields: {
                    system: <SingleCodeField>{
                        generalProperties: {
                            fieldApiName: 'system',
                            fieldName: 'System',
                            fieldType: 'SingleCodeField',
                            isArray: false,
                            isGroup: false,
                            allowedOthers: true
                        },
                        data: options.telecomSystem ?? []
                    },
                    use: <SingleCodeField>{
                        generalProperties: {
                            fieldApiName: 'use',
                            fieldName: 'Use',
                            fieldType: 'SingleCodeField',
                            isArray: false,
                            isGroup: false,
                            allowedOthers: true
                        },
                        data: options.telecomUse ?? []
                    },
                    value: <IndividualField>{
                        generalProperties: {
                            fieldApiName: 'value',
                            fieldName: 'Value',
                            fieldType: 'IndividualField',
                            inputType: 'text',
                            isArray: false,
                            isGroup: false,
                            validations: [{ type: 'default', name: 'required', isFunction: false }]
                        }
                    }
                }
            },
            <GroupField>{
                generalProperties: {
                    fieldApiName: 'availability',
                    fieldName: 'Availability',
                    fieldType: 'IndividualField',
                    isArray: true,
                    isGroup: true
                },
                keys: ['daysOfWeek', 'allDay', 'availableStartTime', 'availableEndTime'],
                groupFields: {
                    daysOfWeek: <SingleCodeField>{
                        generalProperties: {
                            fieldApiName: 'daysOfWeek',
                            fieldName: 'Days of week',
                            fieldType: 'SingleCodeField',
                            isArray: false,
                            isGroup: false,
                            allowedOthers: true
                        },
                        data: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
                    },
                    allDay: <IndividualField>{
                        generalProperties: {
                            fieldApiName: 'allDay',
                            fieldName: 'All day',
                            fieldType: 'IndividualField',
                            inputType: 'toggle',
                            isArray: false,
                            isGroup: false
                        }
                    },
                    availableStartTime: <IndividualField>{
                        generalProperties: {
                            fieldApiName: 'availableStartTime',
                            fieldName: 'Start time',
                            fieldType: 'IndividualField',
                            inputType: 'time',
                            isArray: false,
                            isGroup: false
                        }
                    },
                    availableEndTime: <IndividualField>{
                        generalProperties: {
                            fieldApiName: 'availableEndTime',
                            fieldName: 'End time',
                            fieldType: 'IndividualField',
                            inputType: 'time',
                            isArray: false,
                            isGroup: false
                        }
                    }
                }
            },
            <GroupField>{
                generalProperties: {
                    fieldApiName: 'notAvailable',
                    fieldName: 'Downtime windows',
                    fieldType: 'IndividualField',
                    isArray: true,
                    isGroup: true
                },
                keys: ['description', 'duringStart', 'duringEnd'],
                groupFields: {
                    description: <IndividualField>{
                        generalProperties: {
                            fieldApiName: 'description',
                            fieldName: 'Reason',
                            fieldType: 'IndividualField',
                            inputType: 'text',
                            isArray: false,
                            isGroup: false
                        }
                    },
                    duringStart: <IndividualField>{
                        generalProperties: {
                            fieldApiName: 'duringStart',
                            fieldName: 'Start date',
                            fieldType: 'IndividualField',
                            inputType: 'date',
                            isArray: false,
                            isGroup: false
                        }
                    },
                    duringEnd: <IndividualField>{
                        generalProperties: {
                            fieldApiName: 'duringEnd',
                            fieldName: 'End date',
                            fieldType: 'IndividualField',
                            inputType: 'date',
                            isArray: false,
                            isGroup: false
                        }
                    }
                }
            },
            <IndividualField>{
                generalProperties: {
                    fieldApiName: 'comment',
                    fieldName: 'Public note',
                    fieldType: 'IndividualField',
                    inputType: 'textarea',
                    isArray: false,
                    isGroup: false
                }
            },
            <IndividualField>{
                generalProperties: {
                    fieldApiName: 'extraDetails',
                    fieldName: 'Internal details',
                    fieldType: 'IndividualField',
                    inputType: 'textarea',
                    isArray: false,
                    isGroup: false
                }
            },
            {
                generalProperties: {
                    fieldApiName: 'assignedPractitioners',
                    fieldName: 'Assigned practitioners',
                    fieldType: 'IndividualReferenceField',
                    isArray: true,
                    isGroup: false,
                    allowedOthers: false
                },
                data: options.practitionerOptions ?? []
            }
        ];
    }

    private deepClone<T>(value: T): T {
        return value ? JSON.parse(JSON.stringify(value)) : value;
    }
}



